export type AppLocale = "es" | "en";

export const LOCALE_STORAGE_KEY = "podscope.locale";
export const PODSCOPE_COMPANY_SLUG = "podscope";

const listeners = new Set<() => void>();

export function readStoredLocale(): AppLocale {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY) === "en" ? "en" : "es";
  } catch {
    return "es";
  }
}

export function setStoredLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore private-mode storage failures; the in-memory listeners still update.
  }
  listeners.forEach((listener) => listener());
}

export function subscribeStoredLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** App route roots. Two- and three-segment paths outside this set are public media kits. */
const APP_ROOTS = new Set([
  "auth",
  "dashboard",
  "chat",
  "inicio",
  "explorar",
  "podcasts",
  "mis-podcasts",
  "mi-contenido",
  "campanas",
  "settings",
  "admin",
  "elecciones",
  "papi-ricky",
  "temp",
  "marcas",
  "review",
  "programs",
  "programs-limited",
  "channels",
  "ayuda",
  "legal",
  "social",
  "explorador",
  "tracker",
  "tonybet",
  "clips",
  "dev",
  "invite",
]);

export function isPublicReportPath(pathname: string): boolean {
  if (pathname.startsWith("/reportes/marcas/")) return true;
  if (/^\/colecciones\/[^/]+/.test(pathname)) return true;
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "programa" && parts[2] === "media-kit") return true;
  if (parts.includes("brands") && parts.length >= 4) return true;
  if (
    parts.length >= 2 &&
    parts.length <= 3 &&
    !APP_ROOTS.has(parts[0])
  ) {
    return true;
  }
  return false;
}

/** Elections is a public Podscope page that opens in English. */
export function electionsLocaleDefaults(pathname: string): {
  publicSurface: boolean;
  companySlug: string | null;
  defaultLocale: AppLocale | null;
} {
  if (pathname === "/elecciones") {
    return {
      publicSurface: true,
      companySlug: PODSCOPE_COMPANY_SLUG,
      defaultLocale: "en",
    };
  }
  return { publicSurface: false, companySlug: null, defaultLocale: null };
}

export function resolveAppLocale(input: {
  stored: AppLocale;
  viewerCompanySlug: string | null;
  reportCompanySlug: string | null;
  publicSurface: boolean;
  langParam: string | null;
  defaultLocale?: AppLocale | null;
}): AppLocale {
  if (input.publicSurface && input.langParam === "en") return "en";
  if (input.publicSurface && input.langParam === "es") return "es";
  if (
    input.publicSurface &&
    input.reportCompanySlug === PODSCOPE_COMPANY_SLUG &&
    input.stored === "en"
  ) {
    return "en";
  }
  if (
    !input.publicSurface &&
    input.viewerCompanySlug === PODSCOPE_COMPANY_SLUG &&
    input.stored === "en"
  ) {
    return "en";
  }
  if (input.publicSurface && input.defaultLocale) return input.defaultLocale;
  return "es";
}

export function canShowLanguageToggle(companySlug: string | null | undefined): boolean {
  return companySlug === PODSCOPE_COMPANY_SLUG;
}

export function withEnglishQuery(url: string, locale: AppLocale): string {
  if (locale !== "en" || !url) return url;
  try {
    const base =
      typeof window !== "undefined" ? window.location.origin : "https://podscope.co";
    const parsed = new URL(url, base);
    parsed.searchParams.set("lang", "en");
    if (/^https?:\/\//i.test(url)) return parsed.toString();
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}
